import { MessageSquarePlus, X } from "lucide-react"
import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { graphql, useLazyLoadQuery, useMutation, useRefetchableFragment } from "react-relay"
import { MemoryRouter } from "react-router-dom"
import { EMBEDDED_CHAT_WIDTH } from "src/common/layout-constants"
import { cn, getRelayErrorMessage } from "src/common/utils"
import { ChatInput } from "src/components/ui/chat/chat-input"
import { ThinkingContainer } from "src/components/ui/chat/ThinkingContainer"
import { useEmbeddedChat } from "src/context/EmbeddedChatContext"
import { useMe } from "src/context/MeContext"
import { getLastAgentResponse, requestChatNotificationPermission, useChatNotification } from "src/hooks/use-chat-notification"
import { usePusherEvent } from "src/hooks/use-pusher-event"
import { useToast } from "src/hooks/use-toast"
import EmptyStateChat from "src/pages/AskPage/components/EmptyStateChat"
import LlmConversationMessageList from "src/pages/AskPage/components/LlmConversationMessageList"
import { useInterruptAgentMutation } from "src/pages/AskPage/hooks/useInterruptAgentMutation"
import { useSendLlmMessage } from "src/pages/AskPage/hooks/useSendLlmMessage"

import type {
  EmbeddedChat_conversation$key,
} from "./__generated__/EmbeddedChat_conversation.graphql"
import type {
  EmbeddedChatConversationQuery,
} from "./__generated__/EmbeddedChatConversationQuery.graphql"
import type { EmbeddedChatSendMessageMutation } from "./__generated__/EmbeddedChatSendMessageMutation.graphql"
import { ThreadSelector } from "./ThreadSelector"
import { useEmbeddedChatBreakpoint } from "./useEmbeddedChatBreakpoint"
import { useEmbeddedChatKeyboard } from "./useEmbeddedChatKeyboard"

const SWIPE_THRESHOLD = 100

// Routes where embedded chat should be hidden (they have their own chat UI)
const DISABLED_ROUTES = ["/chat"]

// Custom event for navigation changes (singleton pattern to avoid multiple patches)
const NAVIGATION_EVENT = "embedded-chat-navigation"
let historyPatched = false

function ensureHistoryPatched() {
  if (typeof window === "undefined" || historyPatched) return
  historyPatched = true

  const originalPushState = history.pushState
  const originalReplaceState = history.replaceState

  history.pushState = function (...args) {
    originalPushState.apply(this, args)
    window.dispatchEvent(new CustomEvent(NAVIGATION_EVENT))
  }

  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args)
    window.dispatchEvent(new CustomEvent(NAVIGATION_EVENT))
  }
}

// Hook to track current pathname (works outside React Router context)
function usePathname() {
  const [pathname, setPathname] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : "/"
  )

  useEffect(() => {
    ensureHistoryPatched()

    const handleLocationChange = () => {
      setPathname(window.location.pathname)
    }

    window.addEventListener("popstate", handleLocationChange)
    window.addEventListener(NAVIGATION_EVENT, handleLocationChange)

    return () => {
      window.removeEventListener("popstate", handleLocationChange)
      window.removeEventListener(NAVIGATION_EVENT, handleLocationChange)
    }
  }, [])

  return pathname
}

// Reuse existing query pattern from LlmConversationChat
const embeddedChatConversationQuery = graphql`
  query EmbeddedChatConversationQuery($id: GlobalID!) {
    node(id: $id) {
      ... on GraphQLLlmConversation {
        ...EmbeddedChat_conversation
      }
    }
  }
`

// Reuse the same fragment fields as LlmConversationChatContent
const embeddedChatConversationFragment = graphql`
  fragment EmbeddedChat_conversation on GraphQLLlmConversation
  @refetchable(queryName: "EmbeddedChatConversationRefetchQuery") {
    id
    llmInteractions(last: 10) {
      id
      status
      statusMessage
    }
    messages {
      id
      messageRole
      messageType
      content
      messageMetadata {
        usage {
          billingTokens
        }
      }
    }
    ...LlmConversationMessageList_llmConversation
  }
`

// Mutation for sending initial message (creates conversation)
const sendMessageMutation = graphql`
  mutation EmbeddedChatSendMessageMutation($input: SendMessageInput!) {
    sendMessage(input: $input) {
      userMessage {
        id
        messageRole
        content
        createdAt
      }
      llmConversation {
        id
        title
        createdAt
        updatedAt
      }
    }
  }
`

interface ConversationContentProps {
  conversationId: string
}

const ConversationContent = React.memo<ConversationContentProps>(
  function ConversationContent({ conversationId }) {
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const me = useMe()

    // Track browser hash for permalink scrolling (since we're inside MemoryRouter)
    const [browserHash, setBrowserHash] = useState(() =>
      typeof window !== "undefined" ? window.location.hash : ""
    )
    useEffect(() => {
      const handleHashChange = () => setBrowserHash(window.location.hash)
      window.addEventListener("hashchange", handleHashChange)
      return () => window.removeEventListener("hashchange", handleHashChange)
    }, [])

    // Query the conversation using existing pattern
    const data = useLazyLoadQuery<EmbeddedChatConversationQuery>(
      embeddedChatConversationQuery,
      { id: conversationId },
      { fetchPolicy: "store-and-network" }
    )

    // Use refetchable fragment (same pattern as LlmConversationChat)
    const [conversation, refetch] = useRefetchableFragment<
      EmbeddedChatConversationQuery,
      EmbeddedChat_conversation$key
    >(embeddedChatConversationFragment, data.node)

    const refetchConversation = useCallback(() => {
      startTransition(() => {
        refetch({}, { fetchPolicy: "network-only" })
      })
    }, [refetch])

    // Reuse the existing send message hook
    const { sendMessage, isSending } = useSendLlmMessage({
      llmConversationId: conversationId,
    })

    // Listen for Pusher updates (same as LlmConversationChat)
    usePusherEvent<{ llm_conversation_id: string }>({
      channelId: conversationId,
      eventType: "LLM_CONVERSATION_UPDATED",
      onEvent: useCallback(() => {
        refetchConversation()
      }, [refetchConversation]),
    })

    // Get the active interaction ID and status message
    const { activeInteractionId, activeStatusMessage } = useMemo(() => {
      const activeInteraction = conversation?.llmInteractions?.find(
        (interaction) =>
          interaction.status === "PENDING" || interaction.status === "IN_PROGRESS"
      )
      return {
        activeInteractionId: activeInteraction?.id,
        activeStatusMessage: activeInteraction?.statusMessage ?? null,
      }
    }, [conversation?.llmInteractions])

    const hasActiveInteraction = Boolean(activeInteractionId)

    // Track when we're waiting for interaction to become active after sending
    const [isWaitingForInteraction, setIsWaitingForInteraction] = useState(false)

    useEffect(() => {
      if (hasActiveInteraction && isWaitingForInteraction) {
        setIsWaitingForInteraction(false)
      }
    }, [hasActiveInteraction, isWaitingForInteraction])

    const { toast } = useToast()

    useEffect(() => {
      if (!isWaitingForInteraction || hasActiveInteraction) return
      const timeoutId = setTimeout(() => {
        setIsWaitingForInteraction(false)
        toast({
          title: "Error",
          description: "Message could not be processed. Please try again.",
          variant: "error",
        })
      }, 30_000)
      return () => clearTimeout(timeoutId)
    }, [isWaitingForInteraction, hasActiveInteraction, toast])

    // Check if the last message is complete (has metadata and usage data)
    const isLastMessageComplete = useMemo(() => {
      const messages = conversation?.messages
      if (!messages || messages.length === 0) return false
      const lastMessage = messages[messages.length - 1]
      if (!lastMessage) return false
      if (lastMessage.messageRole !== "AGENT") return false
      if (lastMessage.messageType === "AGENT_PROCESS") return false
      return Boolean(lastMessage.messageMetadata) && Boolean(lastMessage.content && lastMessage.content.length > 0)
    }, [conversation?.messages])

    const lastAgentResponse = useMemo(
      () => getLastAgentResponse(conversation?.messages),
      [conversation?.messages],
    )

    const combinedSendingState = isSending || isWaitingForInteraction || (hasActiveInteraction && !isLastMessageComplete)

    const { requestNotificationPermission } = useChatNotification({
      isWaitingForResponse: combinedSendingState,
      lastResponseText: lastAgentResponse.text,
      lastResponseId: lastAgentResponse.id,
    })

    const handleSend = useCallback(
      async (content: string) => {
        const trimmedMessage = content.trim()
        if (!trimmedMessage) return
        requestNotificationPermission()
        setIsWaitingForInteraction(true)
        try {
          await sendMessage(trimmedMessage)
          refetchConversation()
        } catch (error) {
          setIsWaitingForInteraction(false)
          const errorMessage = error instanceof Error ? getRelayErrorMessage(error) : "Failed to send message"
          toast({
            description: errorMessage,
            variant: "error",
          })
        }
      },
      [requestNotificationPermission, sendMessage, refetchConversation, toast]
    )

    // Interrupt agent mutation
    const { handleInterruptAgent } = useInterruptAgentMutation()
    const [isInterrupting, setIsInterrupting] = useState(false)

    useEffect(() => {
      if (!hasActiveInteraction && isInterrupting) {
        setIsInterrupting(false)
      }
    }, [hasActiveInteraction, isInterrupting])

    const handleInterrupt = useCallback(() => {
      if (activeInteractionId && conversation) {
        setIsInterrupting(true)
        handleInterruptAgent(conversation.id, undefined, conversation.id, activeInteractionId)
      }
    }, [conversation, activeInteractionId, handleInterruptAgent])

    // Stop & Send workflow state
    const [pendingMessage, setPendingMessage] = useState<string | null>(null)
    const [waitingToSendAfterInterrupt, setWaitingToSendAfterInterrupt] = useState(false)
    const chatInputRef = useRef<HTMLTextAreaElement & { setInputMessage?: (msg: string) => void }>(null)

    const handleSubmitWhileDisabled = useCallback((message: string) => {
      setPendingMessage(message)
    }, [])

    const handleStopAndSend = useCallback(() => {
      if (pendingMessage && activeInteractionId && conversation) {
        chatInputRef.current?.setInputMessage?.("")
        setIsInterrupting(true)
        setWaitingToSendAfterInterrupt(true)
        handleInterruptAgent(conversation.id, undefined, conversation.id, activeInteractionId)
      }
    }, [pendingMessage, activeInteractionId, handleInterruptAgent, conversation])

    // Send the pending message once interrupt completes
    useEffect(() => {
      if (waitingToSendAfterInterrupt && !hasActiveInteraction && pendingMessage) {
        handleSend(pendingMessage)
        setPendingMessage(null)
        setWaitingToSendAfterInterrupt(false)
      }
    }, [waitingToSendAfterInterrupt, hasActiveInteraction, pendingMessage, handleSend])

    const handleDismissWarning = useCallback(() => {
      setPendingMessage(null)
      setWaitingToSendAfterInterrupt(false)
    }, [])

    // Reset pending message when thinking completes naturally
    useEffect(() => {
      if (!hasActiveInteraction && pendingMessage && !waitingToSendAfterInterrupt) {
        setPendingMessage(null)
      }
    }, [hasActiveInteraction, pendingMessage, waitingToSendAfterInterrupt])

    // Polling fallback: periodically refetch while agent is active
    useEffect(() => {
      if (!hasActiveInteraction) return
      const interval = setInterval(() => {
        refetchConversation()
      }, 20_000)
      return () => clearInterval(interval)
    }, [hasActiveInteraction, refetchConversation])

    const userInitials = `${me?.firstName?.[0] ?? ""}${me?.lastName?.[0] ?? ""}`

    if (!conversation) {
      return null
    }

    return (
      <>
        {/* Messages area */}
        <div className="flex-1 overflow-hidden">
          <LlmConversationMessageList
            llmConversation={conversation}
            userAvatar={me?.pictureUrl ?? undefined}
            userInitials={userInitials}
            messagesEndRef={messagesEndRef}
            isPending={combinedSendingState}
            activeStatusMessage={activeStatusMessage}
            hideAvatars
            urlHash={browserHash}
          />
        </div>

        {/* Input area with interrupt/stop-and-send support */}
        <div className="p-3 border-t border-border">
          <ThinkingContainer
            isThinking={combinedSendingState}
            pendingMessage={pendingMessage}
            onStopAndSend={handleStopAndSend}
            onDismissWarning={handleDismissWarning}
            isStopAndSendPending={waitingToSendAfterInterrupt}
          >
            <form className="rounded-2xl bg-zinc-900/60 border border-border p-1.5 shadow-2xl focus-within:border-primary/50 focus-within:shadow-primary/20 transition-all">
              <ChatInput
                ref={chatInputRef}
                placeholder="Type a message..."
                onSend={handleSend}
                sendDisabled={combinedSendingState}
                onSubmitWhileDisabled={handleSubmitWhileDisabled}
                showInterrupt={combinedSendingState}
                onInterrupt={handleInterrupt}
                isInterrupting={isInterrupting}
                className="border-0 bg-transparent focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </form>
          </ThinkingContainer>
        </div>
      </>
    )
  }
)


export const EmbeddedChat = React.memo(function EmbeddedChat() {
  const {
    isOpen,
    isDisabled,
    close,
    getConversationForPath,
    setConversationForPath,
    handlePathChange,
    contexts,
  } = useEmbeddedChat()
  const breakpoint = useEmbeddedChatBreakpoint()
  const pathname = usePathname()
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname
  const { toast } = useToast()
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Notify context of path changes (auto-closes chat on navigation)
  useEffect(() => {
    handlePathChange(pathname)
  }, [pathname, handlePathChange])

  // Get conversation for current path
  const activeConversationId = getConversationForPath(pathname)

  // Compute metadata filter from registered contexts for thread selector
  const metadataFilter = useMemo(() => {
    const filter: Record<string, unknown> = {}
    for (const [key, ctx] of Object.entries(contexts)) {
      if (ctx.data) filter[key] = ctx.data
    }
    return Object.keys(filter).length > 0 ? filter : null
  }, [contexts])

  // Track fetch key to refetch thread list when new conversations are created
  const [threadFetchKey, setThreadFetchKey] = useState(0)

  // Check if we're on a route that has its own chat UI
  const isOnDisabledRoute = DISABLED_ROUTES.some((route) =>
    pathname.startsWith(route)
  )

  // Swipe gesture state for mobile
  const [swipeY, setSwipeY] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const touchStartY = useRef(0)

  // Send message mutation for creating new conversations
  const [commitSendMessage, isSendingNewMessage] = useMutation<EmbeddedChatSendMessageMutation>(
    sendMessageMutation
  )

  // Register keyboard shortcuts
  useEmbeddedChatKeyboard({ breakpoint })

  // Handle backdrop click (tablet mode)
  const handleBackdropClick = useCallback(() => {
    if (breakpoint === "tablet") {
      close()
    }
  }, [breakpoint, close])

  // Touch handlers for mobile swipe-to-dismiss
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (breakpoint !== "mobile") return
      touchStartY.current = e.touches[0].clientY
      setIsSwiping(true)
    },
    [breakpoint]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isSwiping || breakpoint !== "mobile") return
      const deltaY = e.touches[0].clientY - touchStartY.current
      if (deltaY > 0) {
        setSwipeY(deltaY)
      }
    },
    [isSwiping, breakpoint]
  )

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping) return
    setIsSwiping(false)

    if (swipeY > SWIPE_THRESHOLD) {
      close()
    }
    setSwipeY(0)
  }, [isSwiping, swipeY, close])

  const handleConversationCreated = useCallback((id: string) => {
    setConversationForPath(pathnameRef.current, id)
    setThreadFetchKey((k) => k + 1)
  }, [setConversationForPath])

  const handleNewConversation = useCallback(() => {
    setConversationForPath(pathnameRef.current, null)
  }, [setConversationForPath])

  // Handle sending first message (creates conversation)
  const handleSendNewMessage = useCallback(
    async (content: string) => {
      const trimmedMessage = content.trim()
      if (!trimmedMessage) return
      requestChatNotificationPermission()

      // Combine all registered context strings
      const contextStrings = Object.values(contexts)
        .map((ctx) => ctx.contextString)
        .filter(Boolean)
      const combinedContext =
        contextStrings.length > 0 ? contextStrings.join("\n\n") : undefined

      // Namespace all data objects by context key to prevent collisions
      const mergedData = Object.entries(contexts).reduce<Record<string, unknown>>(
        (acc, [key, ctx]) => {
          if (ctx.data) {
            acc[key] = ctx.data
          }
          return acc
        },
        {}
      )
      let contextData: string | undefined
      if (Object.keys(mergedData).length > 0) {
        try {
          contextData = JSON.stringify(mergedData)
        } catch (err) {
          console.error("Failed to stringify embedded chat context data", err)
          toast({
            title: "Warning",
            description: "Some page context could not be attached to this conversation.",
            variant: "error",
          })
          contextData = undefined
        }
      }

      // Extract jobRunId from registered contexts if available
      const investigationData = mergedData.investigation as
        | { jobRunId?: string; type?: string }
        | undefined
      const jobRunId = investigationData?.jobRunId

      commitSendMessage({
        variables: {
          input: {
            message: trimmedMessage,
            jobRunId: jobRunId ?? undefined,
            context: combinedContext,
            contextData,
          },
        },
        onCompleted: (response) => {
          const newConversationId = response.sendMessage.llmConversation?.id
          if (newConversationId) {
            handleConversationCreated(newConversationId)
          }
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: "Failed to send message: " + error.message,
            variant: "error",
          })
        },
      })
    },
    [commitSendMessage, handleConversationCreated, toast, contexts]
  )

  // Don't render if disabled or on a route with its own chat UI
  if (isDisabled || isOnDisabledRoute) {
    return null
  }

  // Calculate transform for swipe gesture
  const swipeTransform = isSwiping ? `translateY(${swipeY}px)` : undefined

  // Common drawer content
  const drawerContent = (
    <>
      {/* Radial glow effect - matches home page styling */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.08)_0%,rgba(0,0,0,0)_50%)] pointer-events-none" />

      {/* Floating action buttons */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
        {metadataFilter && (
          <ThreadSelector
            activeConversationId={activeConversationId}
            onSelectThread={(id) => setConversationForPath(pathnameRef.current, id)}
            metadataFilter={metadataFilter}
            fetchKey={threadFetchKey}
          />
        )}
        {activeConversationId && (
          <button
            className="h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-white hover:bg-muted transition-colors"
            onClick={handleNewConversation}
            title="New conversation"
          >
            <MessageSquarePlus className="h-4 w-4" />
          </button>
        )}
        <button
          className="h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-white hover:bg-muted transition-colors"
          onClick={close}
          title="Close chat (Esc)"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Drag handle for mobile */}
      {breakpoint === "mobile" && (
        <div
          className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-12 h-1.5 bg-neutral-500 rounded-full" />
        </div>
      )}

      {/* Content based on whether we have a conversation */}
      <React.Suspense
        fallback={
          <div className="flex-1 flex items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-electric" />
          </div>
        }
      >
        <MemoryRouter>
          {activeConversationId ? (
            <ConversationContent conversationId={activeConversationId} />
          ) : (
            <EmptyStateChat
              onSendMessage={handleSendNewMessage}
              messagesEndRef={messagesEndRef}
              tips={[]}
              isSending={isSendingNewMessage}
              compact
            />
          )}
        </MemoryRouter>
      </React.Suspense>
    </>
  )

  // Desktop mode: persistent drawer on right
  if (breakpoint === "desktop") {
    return (
      <div
        className={cn(
          "fixed top-11 right-0 h-[calc(100vh-2.75rem)] bg-popover border-l border-border flex flex-col z-50",
          "transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
        style={{ width: EMBEDDED_CHAT_WIDTH }}
        role="dialog"
        aria-label="Chat assistant"
      >
        {drawerContent}
      </div>
    )
  }

  // Tablet mode: overlay drawer with backdrop
  if (breakpoint === "tablet") {
    return (
      <>
        {/* Backdrop */}
        <div
          className={cn(
            "fixed inset-0 bg-black/50 z-40 transition-opacity duration-300",
            isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          onClick={handleBackdropClick}
          aria-hidden="true"
        />

        {/* Drawer */}
        <div
          className={cn(
            "fixed top-0 right-0 h-screen bg-popover border-l border-border flex flex-col z-50",
            "transition-transform duration-300 ease-in-out",
            isOpen ? "translate-x-0" : "translate-x-full"
          )}
          style={{ width: EMBEDDED_CHAT_WIDTH }}
          role="dialog"
          aria-label="Chat assistant"
        >
          {drawerContent}
        </div>
      </>
    )
  }

  // Mobile mode: fullscreen overlay from bottom
  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-40 transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        aria-hidden="true"
      />

      {/* Fullscreen drawer */}
      <div
        className={cn(
          "fixed bottom-0 left-0 right-0 h-screen bg-popover flex flex-col z-50",
          "transition-transform duration-300 ease-in-out",
          isOpen ? "translate-y-0" : "translate-y-full"
        )}
        style={{ transform: swipeTransform }}
        role="dialog"
        aria-label="Chat assistant"
        aria-modal="true"
      >
        {drawerContent}
      </div>
    </>
  )
})
